import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";

const inputCls =
  "w-full px-3 mt-1 text-sm py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
const labelCls = "block text-xs font-medium text-gray-600";

const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState("");
  const [currentHostPhone, setCurrentHostPhone] = useState(null);
  const [form, setForm] = useState(null);
  // Why the status is moving. Required only when it actually moves — see
  // handleSave. The backend already accepts and audit-logs this field; it was
  // simply never populated from this screen.
  const [statusReason, setStatusReason] = useState("");
  // What the status was when this form loaded, so "did it change" is a real
  // comparison rather than a guess.
  const originalStatus = useRef(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await func.get(`${urls.GET_EVENT_BY_ID_URL}${id}`);
        if (res?.status === 200 && res?.data?.event) {
          const ev = res.data.event;
          setSlug(ev.slug);
          setCurrentHostPhone(ev.hostMatchmakerId?._phoneNumber || null);
          setForm({
            publicLabel: ev.publicLabel || "",
            name: ev.name || "",
            startAtLocal: toLocalInput(ev.startAt),
            durationMin: ev.durationMin ?? 120,
            city: ev.city || "",
            community: ev.community || "",
            professionTag: ev.professionTag || "",
            maritalPool: ev.maritalPool || "never_married",
            basePrice: ev.basePrice ?? 299,
            capacityPresenters: ev.capacity?.presenters ?? 24,
            maxGenderRatio: ev.maxGenderRatio ?? 1.3,
            // Load the existing values — initialising these to "" would blank a
            // working joining link the moment anyone saved an unrelated edit.
            meetingUrl: ev.meetingUrl || "",
            meetingPasscode: ev.meetingPasscode || "",
            womenFree: ev.womenFree ?? true,
            hostPhone: "",
            status: ev.status || "draft",
          });
          originalStatus.current = ev.status || "draft";
        } else {
          toast.error("Event not found");
          navigate("/events/list");
        }
      } catch (err) {
        toast.error("Failed to load event");
        navigate("/events/list");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.publicLabel.trim()) return toast.warn("Public label is required");
    if (!form.startAtLocal) return toast.warn("Start date/time is required");

    // ── The status field is not like the others ──────────────────────────────
    // Two screens write Event.status through the same endpoint with different
    // care: the listing has a modal that captures a reason, this form had a
    // bare dropdown that captured nothing. So the same change was audited
    // properly from one screen and logged with reason:null from the other.
    //
    // Worse than the audit gap: only 'approved' and 'live' are served publicly,
    // so moving off either one takes down the landing page, the calendar entry
    // and every registered family's link — instantly, from a dropdown, with no
    // confirmation. A reason is required only when the status actually changed,
    // so an unrelated edit (fixing a typo in the city) never has to justify
    // itself.
    const statusChanged = form.status !== originalStatus.current;
    if (statusChanged) {
      const leavingPublic = ["approved", "live"].includes(originalStatus.current)
        && !["approved", "live"].includes(form.status);
      if (leavingPublic && !window.confirm(
        `This event is currently "${originalStatus.current}" and visible to the public.\n\n` +
        `Moving it to "${form.status}" takes the page down immediately — anyone already ` +
        `registered will find a dead link.\n\nContinue?`
      )) return;
      if (!statusReason.trim()) {
        return toast.warn("Say why the status is changing — it goes in the audit log");
      }
    }

    setSaving(true);
    try {
      const payload = {
        publicLabel: form.publicLabel.trim(),
        name: form.name.trim() || form.publicLabel.trim(),
        startAt: new Date(form.startAtLocal).toISOString(),
        durationMin: Number(form.durationMin) || 120,
        city: form.city.trim() || null,
        community: form.community.trim() || null,
        professionTag: form.professionTag.trim() || null,
        maritalPool: form.maritalPool,
        basePrice: Number(form.basePrice) || 0,
        capacityPresenters: Number(form.capacityPresenters) || 24,
        maxGenderRatio: Number(form.maxGenderRatio) || 1.3,
        meetingUrl: form.meetingUrl.trim() || null,
        meetingPasscode: form.meetingPasscode.trim() || null,
        womenFree: !!form.womenFree,
        status: form.status,
        // Only sent when it means something — the backend logs it verbatim.
        ...(statusChanged ? { statusReason: statusReason.trim() } : {}),
      };
      // Absent flag = leave it alone (matches createEvent.mjs's own rule) —
      // only send hostPhone if this save actually typed a new one.
      if (form.hostPhone.trim()) payload.hostPhone = form.hostPhone.trim();

      const res = await func.put(`${urls.EDIT_EVENT_URL}${id}`, payload);
      if (res?.status === 200) {
        toast.success("Event updated");
        navigate("/events/list");
      } else {
        toast.warn(res?.data?.message || "Failed to update event");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update event");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 flex items-center justify-center">
        <p className="text-white/70 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarDays} className="text-white" />
            <h1 className="text-xl font-bold text-white">Edit Event · /{slug}</h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Identity</h3>
            <p className="text-xs text-gray-400">Slug (/{slug}) is permanent — it is not editable here to avoid breaking a link already shared.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Internal name</label>
                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Public label *</label>
                <input className={inputCls} value={form.publicLabel} onChange={(e) => set("publicLabel", e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Start (your local time) *</label>
                <input type="datetime-local" className={inputCls} value={form.startAtLocal} onChange={(e) => set("startAtLocal", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Duration (minutes)</label>
                <input type="number" className={inputCls} value={form.durationMin} onChange={(e) => set("durationMin", e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Audience (targeting walls)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Community</label>
                <input className={inputCls} value={form.community} onChange={(e) => set("community", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Profession tag</label>
                <input className={inputCls} value={form.professionTag} onChange={(e) => set("professionTag", e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Marital pool</label>
              <select className={inputCls} value={form.maritalPool} onChange={(e) => set("maritalPool", e.target.value)}>
                <option value="never_married">Never married</option>
                <option value="second_marriage">Second marriage</option>
              </select>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Capacity &amp; pricing</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Capacity (presenters)</label>
                <input type="number" className={inputCls} value={form.capacityPresenters} onChange={(e) => set("capacityPresenters", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Max gender ratio</label>
                <input type="number" step="0.1" className={inputCls} value={form.maxGenderRatio} onChange={(e) => set("maxGenderRatio", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Base price (₹)</label>
                <input type="number" className={inputCls} value={form.basePrice} onChange={(e) => set("basePrice", e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.womenFree} onChange={(e) => set("womenFree", e.target.checked)} className="rounded border-gray-300" />
              Women free
            </label>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Host &amp; status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Host phone number</label>
                <input className={inputCls} value={form.hostPhone} onChange={(e) => set("hostPhone", e.target.value)} placeholder={currentHostPhone ? `Current: ${currentHostPhone}` : "Not set"} />
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep the current host. Typing a number here replaces the host, not adds a co-host.</p>
              </div>

            <div className="col-span-2">
              <label className={labelCls}>Joining link (Zoom / Meet)</label>
              <input
                className={inputCls}
                value={form.meetingUrl}
                onChange={(e) => set("meetingUrl", e.target.value)}
                placeholder="https://zoom.us/j/… — where the guests actually see each other"
              />
              <p className="text-xs text-gray-400 mt-1">
                The in-page video embed is not configured, so this link is the only
                video path for the room. Without it, guests get a score screen and
                nowhere to meet.
              </p>
            </div>
            <div>
              <label className={labelCls}>Meeting passcode</label>
              <input
                className={inputCls}
                value={form.meetingPasscode}
                onChange={(e) => set("meetingPasscode", e.target.value)}
                placeholder="Zoom usually needs one"
              />
            </div>

              <div>
                <label className={labelCls}>Status</label>
                <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="live">Live</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Appears only once the status has actually been changed, so an
                unrelated edit never has to justify itself. The listing screen's
                modal asks for the same thing; both now reach the same audit
                log with the same information. */}
            {form.status !== originalStatus.current && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <label className={labelCls}>
                  Why is this changing from “{originalStatus.current}” to “{form.status}”?
                  <span className="text-red-500"> *</span>
                </label>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Goes into the audit log against your name"
                />
                {["approved", "live"].includes(originalStatus.current)
                  && !["approved", "live"].includes(form.status) && (
                  <p className="text-xs text-red-600 mt-2">
                    This event is public right now. Saving this takes the page down
                    immediately and anyone already registered will find a dead link.
                  </p>
                )}
              </div>
            )}
          </section>

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;
